import { useState } from 'react';
import { Bell, LogOut, User } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useUiStore, Section } from '../store/ui';
import { initials } from '../lib/dates';
import TodayView from '../views/TodayView';
import PlanningView from '../views/PlanningView';
import StudentsView from '../views/StudentsView';
import BilanModal from './BilanModal';
import LivretModal from './LivretModal';

const TABS: Array<{ key: Section; label: string }> = [
  { key: 'jour', label: 'Ma journée' },
  { key: 'planning', label: 'Mon planning' },
  { key: 'eleves', label: 'Mes élèves' },
];

export default function Layout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const section = useUiStore((s) => s.section);
  const setSection = useUiStore((s) => s.setSection);
  const [menuOpen, setMenuOpen] = useState(false);

  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Moniteur';
  const avatar = initials(user?.firstName ?? 'M', user?.lastName ?? 'O');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <img src="/logo-permio.png" alt="Permio" className="h-7 w-auto flex-shrink-0" />
              {user?.schoolName && (
                <>
                  <div className="hidden sm:block h-6 w-px bg-slate-200" />
                  <span className="hidden sm:block text-xs text-slate-500 truncate">
                    {user.schoolName}
                  </span>
                </>
              )}
            </div>

            <nav className="hidden lg:flex items-center gap-8 h-16">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setSection(t.key)}
                  className={`h-16 border-b-2 text-sm font-medium transition-colors px-1 ${
                    section === t.key
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button className="relative p-2.5 rounded-lg hover:bg-slate-100 transition-colors">
                <Bell size={20} className="text-slate-600" />
              </button>
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 text-white flex items-center justify-center text-xs font-semibold hover:ring-2 hover:ring-violet-200 transition-all"
                >
                  {avatar}
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-in">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                      <div className="text-sm font-semibold text-slate-900">{name}</div>
                      <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    </div>
                    <div className="p-1.5">
                      <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors text-left">
                        <User size={15} className="text-slate-500" />
                        Mon profil
                      </button>
                      <button
                        onClick={() => logout()}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
                      >
                        <LogOut size={15} />
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Nav mobile */}
          <nav className="lg:hidden flex items-center gap-2 overflow-x-auto pb-3">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setSection(t.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  section === t.key
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {section === 'jour' && <TodayView />}
        {section === 'planning' && <PlanningView />}
        {section === 'eleves' && <StudentsView />}
      </main>

      <BilanModal />
      <LivretModal />
    </div>
  );
}
